// Cube-recompute core, shared by the CLI (demo_scripts/setup_04_aggregate.js) and the web
// backend (the ingest job). Reads back the source's private record containers
// (a source now projects into more than one — diversity/, operations/, …),
// computes the flat qb:Observation aggregates declared in aggregations.json,
// applies small-cell suppression, and PUTs one Turtle file per aggregate to
// /{inst}/diversity/aggregates/. Full recompute each run (cheap at this scale).
//
// `period` is a global dimension appended to every aggregate. `suppressBelow`
// gates contributing records; a sum cube may additionally gate its summed value
// with `suppressMeasureBelow`. An aggregate
// naturally matches only the record type(s) carrying all its dimensions, so a
// gender×nationality aggregate reads diversity records and a diet aggregate reads
// operations records with no per-aggregate container wiring.
//
// It writes ONLY the private aggregates container — never /diversity/shared/.
// Surfacing a new period to partners is a separate governance act (republish).

const CONCURRENCY = 24;

const HEADER = [
  '@prefix adr: <https://data.culturalheritage.network/ontology/adr#> .',
  '@prefix schema: <https://schema.org/> .',
  '@prefix dcterms: <http://purl.org/dc/terms/> .',
  '@prefix qb: <http://purl.org/linked-data/cube#> .',
  '@prefix xsd: <http://www.w3.org/2001/XMLSchema#> .',
  '',
].join('\n');

const slugify = (s) => String(s).replace(/[^0-9A-Za-z]+/g, '-').replace(/^-|-$/g, '');
const sourceComponent = (value) => String(value).toLowerCase()
  .replace(/[^0-9a-z]+/g, '-').replace(/^-+|-+$/g, '');

function parseSubjectBody(body) {
  const out = {};
  for (const stmt of body.split(';')) {
    const s = stmt.trim().replace(/\s*\.\s*$/, '');
    if (!s) continue;
    const sp = s.indexOf(' ');
    const pred = s.slice(0, sp).trim();
    const objsRaw = s.slice(sp + 1).trim();
    if (!pred || pred === 'a') continue;
    const objs = splitObjects(objsRaw).map((raw) => ({ raw, norm: normObject(raw) }));
    (out[pred] ??= []).push(...objs);
  }
  return out;
}

// Parse every generated record subject in either a legacy one-record document
// or a batch. The raw token is retained for verbatim cube re-emission.
export function parseRecordParts(ttl) {
  const parts = [];
  const pattern = /<#([^>]+)>\s+([\s\S]*?)\s*\.\s*(?=<#|$)/g;
  for (const match of ttl.matchAll(pattern)) {
    const record = parseSubjectBody(match[2]);
    const sourceId = record['adr:sourceId']?.[0]?.norm ?? null;
    parts.push({ subject: match[1], sourceId, record });
  }
  return parts;
}

export function parseRecord(ttl) {
  return parseRecordParts(ttl)[0]?.record ?? {};
}

export function mergeRecordParts(parts) {
  const merged = {};
  for (const part of parts) {
    for (const [predicate, values] of Object.entries(part)) {
      const existing = merged[predicate] ??= [];
      for (const value of values) {
        if (!existing.some((v) => v.raw === value.raw)) existing.push(value);
      }
    }
  }
  return merged;
}

// split a comma-separated object list, respecting quoted literals
function splitObjects(s) {
  const parts = []; let cur = '', q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"' && s[i - 1] !== '\\') q = !q;
    if (c === ',' && !q) { parts.push(cur.trim()); cur = ''; }
    else cur += c;
  }
  if (cur.trim()) parts.push(cur.trim());
  return parts;
}

function normObject(raw) {
  if (raw.startsWith('"')) {
    const m = raw.match(/^"((?:[^"\\]|\\.)*)"/);
    return m ? m[1] : raw;
  }
  return raw; // prefixed IRI, e.g. adr:Female
}

function cartesian(lists) {
  return lists.reduce((acc, list) => acc.flatMap((a) => list.map((x) => [...a, x])), [[]]);
}

export function calculateAggregates(records, agg) {
  const globalSuppress = agg.suppressBelow ?? 0;
  return agg.aggregates.map((a) => {
    const slice = { dataSet: a.id, dimensions: agg.period ? [...a.dimensions, agg.period] : a.dimensions,
      theme: a.theme ?? null, suppressBelow: globalSuppress,
      suppressMeasureBelow: a.suppressMeasureBelow ?? 0,
      measure: a.measure ?? agg.measure, reducer: a.reducer ?? 'count' };
    const cells = new Map();
    const diagnostics = {
      totalRecords: records.length, includedRecords: 0, excludedRecords: 0,
      invalidMeasures: { missing: 0, repeated: 0, nonNumeric: 0, negative: 0 },
    };
    for (const rec of records) {
      const dimVals = slice.dimensions.map((d) => rec[d] ?? []);
      if (dimVals.some((v) => v.length === 0)) { diagnostics.excludedRecords++; continue; }
      let increment = 1;
      if (slice.reducer === 'sum') {
        const measures = rec[slice.measure] ?? [];
        let reason = null;
        if (!measures.length) reason = 'missing';
        else if (measures.length !== 1) reason = 'repeated';
        else {
          const lexical = String(measures[0].norm).trim();
          increment = Number(lexical);
          if (!lexical || !Number.isFinite(increment)) reason = 'nonNumeric';
          else if (increment < 0) reason = 'negative';
        }
        if (reason) {
          diagnostics.invalidMeasures[reason]++;
          diagnostics.excludedRecords++;
          continue;
        }
      }
      diagnostics.includedRecords++;
      for (const combo of cartesian(dimVals)) {
        const key = combo.map((c) => c.norm).join(' | ');
        const cell = cells.get(key) ?? {
          norms: combo.map((c) => c.norm), raws: combo.map((c) => c.raw), count: 0, contributions: 0,
        };
        cell.count += increment;
        cell.contributions++;
        cells.set(key, cell);
      }
    }
    const all = [...cells.values()];
    const kept = all.filter((c) => c.contributions >= slice.suppressBelow
      && c.count >= slice.suppressMeasureBelow).sort((a, b) => b.count - a.count);
    return { slice, all, kept, diagnostics };
  });
}

// What one aggregate costs, in the terms the wireframe asks for: how many groups
// the data could produce, how many survive the threshold, and what that loss
// means. The percentage on its own does not carry the judgement — a cube where
// every published group is safe can still say almost nothing — so the verdict is
// computed here, once, and shown wherever a preview is.
export function summarizeCells(all, kept) {
  const possible = all.length;
  const published = kept.length;
  const hidden = possible - published;
  const lossPct = possible ? Math.round((hidden / possible) * 100) : 0;
  const examples = kept.slice(0, 3).map((c) => ({ label: c.norms.join(' · '), count: c.count }));
  return { possible, hidden, published, lossPct, examples, ...verdictFor(published, lossPct) };
}

function verdictFor(published, lossPct) {
  if (!published) return { variant: 'danger', verdict: 'Nothing survives — every group is under the threshold.' };
  if (lossPct >= 50) return { variant: 'warning', verdict: `Safe, but ${lossPct}% is hidden — the breakdown says little.` };
  if (lossPct >= 20) return { variant: 'info', verdict: `Safe; ${lossPct}% is hidden.` };
  return { variant: 'success', verdict: lossPct ? `Safe — only ${lossPct}% is hidden.` : 'Safe — nothing is hidden.' };
}

// Summarise a whole aggregate list against already-projected records, keyed by
// aggregate id. Keyed, not positional: the caller's list and the summary list
// drift apart the moment anything is added or removed.
export function summarizeAggregates(records, agg) {
  const out = {};
  for (const { slice, all, kept, diagnostics } of calculateAggregates(records, agg)) {
    out[slice.dataSet] = { ...summarizeCells(all, kept), ...diagnostics,
      measure: slice.measure, reducer: slice.reducer };
  }
  return out;
}

async function runPool(items, worker) {
  let i = 0; const results = new Array(items.length);
  async function next() {
    while (i < items.length) { const idx = i++; results[idx] = await worker(items[idx]); }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, next));
  return results;
}

export function emitSlice(slice, cells, publisher) {
  const lines = [HEADER, `<#${slice.dataSet}> a qb:DataSet .`, ''];
  let idx = 0;
  for (const cell of cells) {
    const id = `obs-${slugify(slice.dataSet)}-${cell.norms.map(slugify).join('-')}-${idx++}`;
    const triples = [`qb:dataSet <#${slice.dataSet}>`, `dcterms:publisher "${publisher}"`];
    slice.dimensions.forEach((dim, d) => triples.push(`${dim} ${cell.raws[d]}`));
    triples.push(`${slice.measure} ${cell.count}`);
    lines.push(`<#${id}>\n  a qb:Observation ;\n  ${triples.join(' ;\n  ')} .`, '');
  }
  return lines.join('\n') + '\n';
}

export const podBaseOf = (connector) => connector.podBase.replace(/\/$/, '') + '/';
export const aggregatesContainerOf = (connector, agg = {}) =>
  `${podBaseOf(connector)}${agg.container ?? 'diversity/aggregates/'}`;

// Read the source's private records back out of the Pod and merge the sibling
// parts of each one into a single record. Separated from recompute because
// previewing an aggregate needs exactly this and must write nothing: the read is
// the expensive half, and it is the half that is safe to repeat.
export async function readProjectedRecords({ connector, recordContainers, authFetch }) {
  const podBase = podBaseOf(connector);
  // Default to diversity/ alone when no containers are supplied (single-record
  // fallback), else read every record-type container the source projects into.
  const containers = (recordContainers?.length ? recordContainers : ['diversity/'])
    .map((entry) => typeof entry === 'string'
      ? { sourceId: null, url: `${podBase}${entry}` }
      : { sourceId: entry.sourceId, url: `${podBase}${entry.container}` });

  // discover records across every container via ldp:contains
  const recordUrls = [];
  const seen = new Set();
  for (const container of containers) {
    const listRes = await authFetch(container.url, { headers: { Accept: 'text/turtle' } });
    const listBody = await listRes.text();
    for (const m of listBody.matchAll(/<([^>]*\.ttl)>/g)) {
      const href = new URL(m[1], container.url).href;
      const name = href.split('/').at(-1);
      if (container.sourceId && name.includes('--batch-')
        && !name.startsWith(`${sourceComponent(container.sourceId)}--`)) continue;
      const identity = `${container.sourceId ?? ''}\u0000${href}`;
      if (!seen.has(identity)) { seen.add(identity); recordUrls.push({ href, sourceId: container.sourceId }); }
    }
  }

  const parsedDocuments = await runPool(recordUrls, async ({ href, sourceId }) => {
    const res = await authFetch(href, { headers: { Accept: 'text/turtle' } });
    const ttl = await res.text();
    return parseRecordParts(ttl).map((part) => ({
      key: `${sourceId ?? ''}\u0000${part.sourceId ?? href.split('/').at(-1)}`,
      identity: `${sourceId ?? ''}\u0000${href.replace(/[^/]+$/, '')}\u0000${part.subject}`,
      ownerSourceId: sourceId,
      record: part.record,
    }));
  });
  const byKey = new Map();
  const identities = new Set();
  const sourceStats = {};
  for (const { href, sourceId } of recordUrls) {
    if (!sourceId) continue;
    const stats = sourceStats[sourceId] ??= { records: 0, documents: 0 };
    stats.documents++;
  }
  let recordCount = 0;
  for (const part of parsedDocuments.flat()) {
    if (identities.has(part.identity)) throw new Error(`Duplicate projected record identity: ${part.identity.split('\u0000').at(-1)}`);
    identities.add(part.identity);
    recordCount++;
    if (part.ownerSourceId) (sourceStats[part.ownerSourceId] ??= { records: 0, documents: 0 }).records++;
    (byKey.get(part.key) ?? byKey.set(part.key, []).get(part.key)).push(part.record);
  }
  const records = [...byKey.values()].map(mergeRecordParts);
  const periods = [...new Set(records.flatMap((record) =>
    (record['adr:period'] ?? []).map((value) => value.norm)).filter(Boolean))].sort();
  return { records, recordUrls, sourceStats, periods,
    documentCount: recordUrls.length, recordCount };
}

// Recompute the private aggregate cube. Returns document, RDF-part, and merged
// logical-row counts plus per-slice results so callers never equate files with
// records.
export async function recomputeAggregates({ connector, agg, recordContainers, authFetch, onProgress,
  publisher = connector.label }) {
  const aggregatesContainer = aggregatesContainerOf(connector, agg);
  const { records: parsed, documentCount, recordCount, sourceStats, periods } =
    await readProjectedRecords({ connector, recordContainers, authFetch });

  // ensure aggregates container exists
  await authFetch(aggregatesContainer, { method: 'PUT', headers: { 'Content-Type': 'text/turtle' }, body: '' });

  // Flatten the aggregate list. `period` is a global dimension appended to each;
  // `suppressBelow` is a single global threshold. Every marginal is suppressed
  // independently. A record that lacks any of an aggregate's dimensions is
  // simply not counted for it, so aggregates route to the right record type.
  const calculated = calculateAggregates(parsed, agg);

  const summary = [];
  let sliceIdx = 0;
  for (const { slice, all, kept, diagnostics } of calculated) {
    const ttl = emitSlice(slice, kept, publisher);
    const res = await authFetch(`${aggregatesContainer}${slice.dataSet}.ttl`, {
      method: 'PUT', headers: { 'Content-Type': 'text/turtle' }, body: ttl,
    });
    summary.push({ theme: slice.theme, dataSet: slice.dataSet, measure: slice.measure, reducer: slice.reducer,
      cells: all.length, kept: kept.length, suppressed: all.length - kept.length, ...diagnostics, status: res.status });
    onProgress?.({ done: ++sliceIdx, total: calculated.length, message: 'Recomputing aggregates' });
  }

  return { recordCount, documentCount, logicalRecordCount: parsed.length, sourceStats, periods,
    aggregatesContainer, slices: summary };
}
